import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Segmented } from '@/components/ui/Segmented';
import { api } from '@/api';
import type { RoomSettingsDto } from '@/api/dto';
import {
  type RoomKind,
  type RoomFormat,
  type RoundDuration,
  type RoundCount,
  type VisibilityOption,
  DURATION_OPTIONS,
  ROUND_COUNT_OPTIONS,
  DEFAULT_DURATION,
  DEFAULT_ROUND_COUNT,
  parseMode,
  buildSettings,
} from '@/domain/roomSettings';
import s from './CreateRoomPage.module.css';

const KIND_OPTIONS = [
  { value: 'friends' as RoomKind, label: 'С друзьями' },
  { value: 'solo' as RoomKind, label: 'Соло' },
];

const FORMAT_OPTIONS = [
  { value: 'single' as RoomFormat, label: 'Один раунд' },
  { value: 'match' as RoomFormat, label: 'Матч' },
];

const ROUND_COUNT_OPTS = ROUND_COUNT_OPTIONS.map((n) => ({
  value: n,
  label: String(n),
}));

const DURATION_OPTS = DURATION_OPTIONS.map((sec) => ({
  value: sec,
  label: `${sec} с`,
}));

const VISIBILITY_OPTS = [
  { value: 'Score' as VisibilityOption, label: 'Видно' },
  { value: 'Hidden' as VisibilityOption, label: 'Скрыто' },
];

const SPEED_BONUS_OPTS = [
  { value: false, label: 'Выкл' },
  { value: true, label: 'Вкл' },
];

function pluralRounds(n: number): string {
  if (n >= 2 && n <= 4) return `${n} раунда`;
  return `${n} раундов`;
}

export function CreateRoomPage() {
  const nav = useNavigate();

  const [roomKind, setRoomKind] = useState<RoomKind>('friends');
  const [format, setFormat] = useState<RoomFormat>('single');
  const [duration, setDuration] = useState<RoundDuration>(DEFAULT_DURATION);
  const [roundCount, setRoundCount] = useState<RoundCount>(DEFAULT_ROUND_COUNT);
  const [visibility, setVisibility] = useState<VisibilityOption>('Score');
  const [speedBonus, setSpeedBonus] = useState(false);
  const [dtDefaults, setDtDefaults] = useState<Pick<RoomSettingsDto, 'isPublic'>>({
    isPublic: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.rooms.getDefaults().then((d) => {
      const { roomKind: k, format: f } = parseMode(d.mode);
      setRoomKind(k);
      setFormat(f);
      if ((DURATION_OPTIONS as readonly number[]).includes(d.roundDurationSec)) {
        setDuration(d.roundDurationSec as RoundDuration);
      }
      if ((ROUND_COUNT_OPTIONS as readonly number[]).includes(d.totalRounds)) {
        setRoundCount(d.totalRounds as RoundCount);
      }
      if (d.progressVisibility === 'Score' || d.progressVisibility === 'Hidden') {
        setVisibility(d.progressVisibility);
      }
      setSpeedBonus(d.speedBonus);
      setDtDefaults({ isPublic: d.isPublic });
    }).catch(() => { /* keep defaults */ });
  }, []);

  const isSolo = roomKind === 'solo';
  const isMatch = !isSolo && format === 'match';

  const create = async () => {
    setLoading(true);
    try {
      const dto = buildSettings(roomKind, format, duration, roundCount, visibility, speedBonus, dtDefaults);
      const room = await api.rooms.create(dto);
      nav(`/r/${room.code}`);
    } finally {
      setLoading(false);
    }
  };

  const chips: string[] = [
    isSolo ? 'Соло' : 'С друзьями',
    ...(!isSolo ? [format === 'single' ? 'Один раунд' : 'Матч'] : []),
    ...(isMatch ? [pluralRounds(roundCount)] : []),
    `${duration} с`,
    ...(speedBonus ? ['Бонус скорости'] : []),
    ...(!isSolo ? [visibility === 'Score' ? 'Очки видны' : 'Очки скрыты'] : []),
  ];

  return (
    <div className={s.page}>
      <header className={s.header}>
        <button className={s.backBtn} onClick={() => nav(-1)} aria-label="Назад">←</button>
        <div className={s.headerText}>
          <span className="kicker">Новая игра</span>
          <h1 className={s.title}>Создать комнату</h1>
        </div>
      </header>

      <div className={s.content}>
        <section className={s.section}>
          <span className={s.label}>С кем?</span>
          <Segmented options={KIND_OPTIONS} value={roomKind} onChange={setRoomKind} />
        </section>

        {!isSolo && (
          <section className={s.section}>
            <span className={s.label}>Формат</span>
            <Segmented options={FORMAT_OPTIONS} value={format} onChange={setFormat} />
          </section>
        )}

        {isMatch && (
          <section className={s.section}>
            <span className={s.label}>Раундов</span>
            <Segmented options={ROUND_COUNT_OPTS} value={roundCount} onChange={setRoundCount} />
          </section>
        )}

        <section className={s.section}>
          <span className={s.label}>Время раунда</span>
          <Segmented options={DURATION_OPTS} value={duration} onChange={setDuration} />
        </section>

        <section className={s.section}>
          <span className={s.label}>Бонус за скорость</span>
          <Segmented options={SPEED_BONUS_OPTS} value={speedBonus} onChange={setSpeedBonus} />
        </section>

        {!isSolo && (
          <section className={s.section}>
            <span className={s.label}>Очки соперников</span>
            <Segmented options={VISIBILITY_OPTS} value={visibility} onChange={setVisibility} />
          </section>
        )}

        <div className={s.summary}>
          {chips.map((chip) => (
            <span key={chip} className={s.chip}>{chip}</span>
          ))}
        </div>
      </div>

      <div className={s.sticky}>
        <Button
          variant="primary"
          onClick={() => void create()}
          loading={loading}
          style={{ width: '100%' }}
        >
          {isSolo ? 'Начать игру' : 'Создать комнату'}
        </Button>
      </div>
    </div>
  );
}
